<script lang="ts">
    import cn from 'classnames';
    import { sendRegistrationEvent } from '$lib/services/gtm/methods';

    import { sendRegistrationStats } from '@api/user/stats';
    import { globalStore } from '@components/stores';
    import { COMPETITOR_NAME } from '@shared/constants/alternative';
    import { Competitor } from '@shared/route-types';
    import { URLS } from '@shared/routing';
    import { EventType } from '@shared/types';
    import { Button, ButtonTypes } from '@uikit/button';
    import { Link } from '@uikit/link';

    import { isCustomDirtyrouletteContent } from '../functions';

    import type { AlternativeInfoBlockContent, DirtyrouletteInfoBlockContent } from './types';

    import s from './info-block.module.css';

    export let content: AlternativeInfoBlockContent | DirtyrouletteInfoBlockContent;
    export let competitor: Competitor | undefined;
    export let lang: string | undefined;
    export let otherCompetitors: Competitor[];
    export let classes = '';

    const extContent: DirtyrouletteInfoBlockContent | null = 'intro' in content ? content : null;
    let isCustomContent: boolean;
    $: isCustomContent = Boolean(competitor && isCustomDirtyrouletteContent(competitor, lang ?? 'en') && extContent);

    function onClick() {
        sendRegistrationEvent({ event: EventType.MainButtonClickTryForFree });
        sendRegistrationStats(EventType.MainButtonClickTryForFree, null, $globalStore.isDesktopSite);
        window.scrollTo(0, 0);
    }
</script>

<div class={cn(s.container, classes)}>
    {#if isCustomContent}
        <section class={s.sec0}>
            <h2 class={s.secTitle}>{extContent?.intro.title}</h2>
            <div class={s.row}>
                <p class={s.secText}>{extContent?.intro.text}</p>
            </div>
        </section>
    {/if}
    <section class={cn(s.sec, s.sec1, { [s.extendedContent]: isCustomContent })}>
        <div class={s.row}>
            <p class={s.secPreamble}>{content.best.preamble}</p>
        </div>
        <div class={s.row}>
            <div class={cn(s.col, s.colText)}>
                <h2 class={s.secTitle}>{content.best.title}</h2>
                <p>{content.best.text}</p>
            </div>
            <div class={cn(s.col, s.colImg)}>
                <img
                    src={content.best.imgSrc}
                    alt={content.best.imgAlt}
                    class={s.img}
                    loading="lazy"
                >
            </div>
        </div>
    </section>

    <section class={cn(s.sec, s.sec2)}>
        <h2 class={s.secTitle}>{content.why.title}</h2>
        <div class={s.row}>
            <p class={s.secText}>{content.why.text}</p>
        </div>
        <div class={s.secContent}>
            <article class={cn(s.article, s.row)}>
                <div class={cn(s.col, s.colImg)}>
                    <img
                        src={content.why.article1.imgSrc}
                        alt={content.why.article1.imgAlt}
                        class={s.img}
                        loading="lazy"
                    >
                </div>
                <div class={s.col}>
                    <h3 class={s.articleTitle}>{content.why.article1.title}</h3>
                    <p>{content.why.article1.text}</p>
                </div>
            </article>
            {#if isCustomContent}
                <div class={s.row}>
                    <p class={s.secText}>{extContent?.why.article1.text2}</p>
                </div>
            {/if}
            <article class={cn(s.article, s.row)}>
                <div class={s.col}>
                    <h3 class={s.articleTitle}>{content.why.article2.title}</h3>
                    <p>{content.why.article2.text}</p>
                </div>
                <div class={cn(s.col, s.colImg)}>
                    <img
                        src={content.why.article2.imgSrc}
                        alt={content.why.article2.imgAlt}
                        class={s.img}
                        loading="lazy"
                    >
                </div>
            </article>
            {#if isCustomContent}
                <div class={s.row}>
                    <p class={s.secText}>{extContent?.why.article2.text2}</p>
                </div>
            {/if}
            <article class={cn(s.article, s.row)}>
                <div class={cn(s.col, s.colImg)}>
                    <img
                        src={content.why.article3.imgSrc}
                        alt={content.why.article3.imgAlt}
                        class={s.img}
                        loading="lazy"
                    >
                </div>
                <div class={s.col}>
                    <h3 class={s.articleTitle}>{content.why.article3.title}</h3>
                    <p>{content.why.article3.text}</p>
                    {#if isCustomContent}
                        <h3 class={cn(s.articleTitle, s.midTitle)}>{extContent?.why.article3_1.title}</h3>
                        <p>{extContent?.why.article3_1.text}</p>
                    {/if}
                </div>
            </article>
            <article class={cn(s.article, s.row)}>
                <div class={s.col}>
                    <h3 class={s.articleTitle}>{content.why.article4.title}</h3>
                    <p>{content.why.article4.text}</p>
                </div>
                <div class={cn(s.col, s.colImg)}>
                    <img
                        src={content.why.article4.imgSrc}
                        alt={content.why.article4.imgAlt}
                        class={s.img}
                        loading="lazy"
                    >
                </div>
            </article>
        </div>
    </section>

    <section class={cn(s.sec, s.sec3)}>
        <h2 class={s.secTitle}>{content.try.title}</h2>
        <p class={s.secText}>{content.try.text}</p>
        {#if isCustomContent}
            <p class={s.secText}>{extContent?.try.text2}</p>
        {/if}
        <Button
            text={content.try.buttonLabel}
            color={ButtonTypes.Color.Brand}
            classes={s.button}
            on:click={onClick}
            attrs={{ 'data-testid': 'tryForFreeButton' }}
        />
    </section>

    <section class={cn(s.sec, s.sec4)}>
        <h2 class={s.secTitle}>{content.comparisons.title}</h2>
        <nav>
            <ul class={s.nav}>
                {#each otherCompetitors as otherCompetitor}
                    <li class={s.navItem}>
                        <Link
                            href={URLS.alternative(otherCompetitor)}
                            classes={s.navItemLink}
                        >
                            {COMPETITOR_NAME[otherCompetitor]}
                        </Link>
                    </li>
                {/each}
            </ul>
        </nav>
    </section>
</div>
